import moment from "moment";
import { useMemo } from "react";

import { localStoreRecentClassroomIdsKeyName } from "links/lib/constants";
import { IClassroom } from "links/lib/types";

interface IArgs {
  classrooms: IClassroom[];
  limit?: number;
}

/**
 * useGetRecentlyViewedClassrooms accepts an array of candidate classrooms
 * and compares them to what we may have stored in
 * localStorage.recentClassroomIds (entries are set when a teacher views a
 * classroom). If no/not enough entries are found, it falls back to the most
 * recently updated classroom(s), based on updated_at.
 *
 * @param classrooms The array of classrooms to consider
 * @param limit The maximum number of classrooms to return
 * @returns IClassroom[] of max size: limit
 */
export const useGetRecentlyViewedClassrooms = ({
  classrooms,
  limit = 2,
}: IArgs): IClassroom[] => {
  const recentlyViewedClassrooms = useMemo(() => {
    const recentlyUpdatedClassrooms = classrooms.sort((a, b) => {
      const momentA = moment(a.updated_at);
      const momentB = moment(b.updated_at);
      if (momentA.isBefore(momentB)) return -1;
      if (momentA.isAfter(momentB)) return 1;
      return 0;
    });

    const recentClassroomIdsString = localStorage.getItem(
      localStoreRecentClassroomIdsKeyName
    );
    try {
      const recentClassroomIds: string[] = JSON.parse(
        recentClassroomIdsString || ""
      );
      if (!recentClassroomIds.length) {
        throw new Error("No recent classroom IDs found");
      }

      const recentClassrooms: IClassroom[] = recentClassroomIds
        .reverse()
        .flatMap((classroomId) => {
          return classrooms.find((c) => c.id === classroomId) || [];
        });

      if (recentClassrooms.length < limit) {
        const classroomsToAdd = recentlyUpdatedClassrooms.filter(
          (c) =>
            !recentClassrooms.find(
              (recentClassroom) => recentClassroom.id === c.id
            )
        );
        if (classroomsToAdd.length) recentClassrooms.push(...classroomsToAdd);
      }

      return recentClassrooms.slice(0, limit);
    } catch (ignored) {
      // Do nothing, fallback to most recently updated classroom
    }

    return recentlyUpdatedClassrooms;
  }, [classrooms, limit]);

  return recentlyViewedClassrooms;
};
